// docs/131 the collection log: every catalogue piece my heroes have ever held, on a grid of squares
// split into pages (CL4). A collected piece shows its art in full, with a New tag until I look at
// it here (CL5: hovered with a mouse, or tapped open on touch); a piece beside one collected shows
// faintly, its name and where to find it on hover (CL6); the rest are blank question marks. The
// count for the whole log and each page is here (CL8); the title's Collection button carries the
// New badge. Opened from the main menu, over the dimmed night clearing as Play online is.
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Display, Eyebrow, Modal, Reveal, Tabs, Tooltip } from '../components';
import { useRoute } from '../../core/stores/route';
import { useCollection, isNew } from '../../core/stores/collection';
import { useMyCollectedPieces } from '../../core/subscriptions/hooks';
import { catalogueItem } from '../../core/items/catalogue';
import { countCollected, squareState, theLayout, whereToFind, type Block, type PageId, type SquareState } from '../../core/items/collection';
import { describeItem, shortLine } from '../../core/items/describe';
import { itemSet } from '../../core/items/catalogue';
import { tsToMs } from '../../core/net/connection';
import { usePlace } from '../../core/audio/places';
import { NightClearing } from './NightClearing';
import { ItemBody, ItemIcon, itemRarity } from '../inventory/ItemCard';
import './collection.css';

const fmt = (n: number): string => n.toLocaleString('en-GB');

/** A square was hovered (or left: `def` undefined) / opened; `state` says whether it is collected. */
type OnHover = (def: number | undefined, state?: SquareState, el?: HTMLElement) => void;
type OnOpen = (def: number, state: SquareState) => void;

interface Mine { collected: ReadonlySet<number>; fresh: ReadonlySet<number>; firstAt: ReadonlyMap<number, number | undefined> }

/** My log as sets: what is collected, what still wears its New tag, when each was first held. */
function useMine(): Mine {
  const rows = useMyCollectedPieces();
  const seenHere = useCollection((s) => s.seen);
  return useMemo(() => ({
    collected: new Set(rows.map((r) => r.def)),
    fresh: new Set(rows.filter((r) => isNew(r, seenHere)).map((r) => r.def)),
    firstAt: new Map(rows.map((r) => [r.def, tsToMs(r.firstAt)])),
  }), [rows, seenHere]);
}

/** Whether any collected piece of mine is unseen (the title's New badge, CL8). */
export function useCollectionHasNew(): boolean {
  const rows = useMyCollectedPieces();
  const seenHere = useCollection((s) => s.seen);
  return useMemo(() => rows.some((r) => isNew(r, seenHere)), [rows, seenHere]);
}

/** What a hinted or collected square says on hover (CL5–CL6). */
function SquareTip({ def, state, fresh }: { def: number; state: SquareState; fresh: boolean }) {
  const it = catalogueItem(def);
  if (!it) return null;
  const d = describeItem(it);
  return (
    <div className="fk-coll__tip" data-testid="collection-tip">
      <div className="fk-coll__tip-kind">{d.rarity} · {d.kind}{fresh && <span className="fk-coll__new fk-coll__new--inline">New</span>}</div>
      <div className="fk-coll__tip-line">{shortLine(it)}</div>
      <p className="fk-coll__tip-flavour">“{it.flavour}”</p>
      {state === 'hinted' && <p className="fk-coll__tip-hint">Not yet collected</p>}
    </div>
  );
}

const Square = memo(function Square({ def, state, fresh, onHover, onOpen }: { def: number; state: SquareState; fresh: boolean; onHover: OnHover; onOpen: OnOpen }) {
  const it = catalogueItem(def);
  if (state === 'unknown' || !it) {
    // CL5: no art, no name, no hover
    return <span className="fk-coll__sq is-unknown" aria-label="Unknown piece" data-testid="collection-square" data-state="unknown"><span aria-hidden="true">?</span></span>;
  }
  return (
    <button
      type="button"
      className={`fk-coll__sq is-${state} fk-inv__tile--${itemRarity(def, it.rarity)}`}
      aria-label={`${it.name}${state === 'hinted' ? ' (not yet collected)' : fresh ? ' (new)' : ''}`}
      data-testid="collection-square"
      data-state={state}
      data-def={def}
      data-new={fresh ? 'true' : undefined}
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') onHover(def, state, e.currentTarget); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') onHover(undefined); }}
      // keyboard focus shows what hover shows; a tap's focus (or focus handed back when the card
      // closes after one) does not, or the tip would stay up with no pointer to leave it
      onFocus={(e) => { if (e.currentTarget.matches(':focus-visible')) onHover(def, state, e.currentTarget); }}
      onBlur={() => onHover(undefined)}
      onClick={() => onOpen(def, state)}
    >
      <ItemIcon def={def} tier={it.rarity} scale={2} />
      {fresh && <span className="fk-coll__new" data-testid="collection-new">New</span>}
    </button>
  );
});

function BlockGrid({ block, mine, onHover, onOpen }: { block: Block; mine: Mine; onHover: OnHover; onOpen: OnOpen }) {
  const layout = theLayout();
  const defs = block.rows.flat().filter((d): d is number => d !== null);
  const sets = block.key === 'sets';
  return (
    <section className="fk-coll__block" data-testid="collection-block" data-block={block.key} aria-label={block.title}>
      {!sets && (
        <h3 className="fk-coll__block-head">
          <span>{block.title}</span>
          <span className="fk-coll__count">{countCollected(defs, mine.collected)} / {defs.length}</span>
        </h3>
      )}
      <div className="fk-coll__rows" role="list">
        {block.rows.map((row, r) => {
          const named = sets ? itemSet(catalogueItem(row.find((d) => d !== null) ?? -1)?.set) : undefined;
          return (
            <div key={r} className="fk-coll__row" role="listitem">
              {named && (() => {
                const members = row.filter((d): d is number => d !== null);
                const anyKnown = members.some((d) => squareState(layout, d, mine.collected) !== 'unknown');
                return <span className="fk-coll__set-name" data-testid="collection-set-name"><span className="fk-coll__set-title">{anyKnown ? named.name : 'An unknown set'}</span><span className="fk-coll__count">{countCollected(members, mine.collected)} / {members.length}</span></span>;
              })()}
              <div className="fk-coll__grid" style={{ gridTemplateColumns: `repeat(${block.width}, var(--fk-coll-sq))` }}>
                {row.map((def, c) => def === null
                  ? <span key={c} className="fk-coll__gap" aria-hidden="true" />
                  : <Square key={def} def={def} state={squareState(layout, def, mine.collected)} fresh={mine.fresh.has(def)} onHover={onHover} onOpen={onOpen} />)}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function CollectionScreen() {
  usePlace({ key: 'place.title' });
  const go = useRoute((s) => s.go);
  const layout = theLayout();
  const mine = useMine();
  const see = useCollection((s) => s.see);
  const flush = useCollection((s) => s.flush);
  const [page, setPage] = useState<PageId>(layout.pages[0]!.id);
  const [hover, setHover] = useState<{ def: number; el: HTMLElement }>();
  const [open, setOpen] = useState<number>();

  // what is waiting goes when the screen closes
  useEffect(() => () => flush(), [flush]);

  // CL5: only a collected piece is seen (a hinted one has no row, and must still be New when found)
  const onHover = useCallback<OnHover>((def, state, el) => {
    if (def === undefined || !el) { setHover(undefined); return; }
    setHover({ def, el });
    if (state === 'collected') see(def);
  }, [see]);
  const onOpen = useCallback<OnOpen>((def, state) => { setOpen(def); if (state === 'collected') see(def); }, [see]);

  const current = layout.pages.find((p) => p.id === page) ?? layout.pages[0]!;
  const tabs = layout.pages.map((p) => {
    const n = countCollected(p.defs, mine.collected);
    const fresh = p.defs.some((d) => mine.fresh.has(d));
    return {
      id: p.id,
      label: <span className="fk-coll__tab">{p.title}<span className="fk-coll__tab-count">{n}/{p.defs.length}</span></span>,
      badge: fresh ? <span className="fk-coll__tab-new" aria-label="new pieces" /> : undefined,
      testId: `collection-tab-${p.id}`,
    };
  });
  const total = countCollected([...layout.squares.keys()], mine.collected);
  const shownHover = hover && squareState(layout, hover.def, mine.collected) !== 'unknown' ? hover : undefined;
  const hoverItem = shownHover ? catalogueItem(shownHover.def) : undefined;
  const openItem = open !== undefined ? catalogueItem(open) : undefined;
  const openState = open !== undefined ? squareState(layout, open, mine.collected) : 'unknown';
  const firstAt = open !== undefined ? mine.firstAt.get(open) : undefined;

  return (
    <main className="fk-screen fk-screen--collection fk-hit" data-testid="collection-screen">
      <NightClearing heroes={[]} dim className="fk-coll__backdrop" />
      <div className="fk-coll">
        <Reveal className="fk-coll__head">
          <Eyebrow>Fellowkeep</Eyebrow>
          <Display size="lg" gold as="h1" className="fk-coll__title">Collection</Display>
          <p className="fk-coll__total" data-testid="collection-total"><span>{fmt(total)} of {fmt(layout.total)} collected</span></p>
          <Button small variant="ghost" className="fk-coll__back" onClick={() => go('title')} data-testid="collection-back">‹ Back</Button>
        </Reveal>
        <Tabs tabs={tabs} value={current.id} onChange={(id) => { setHover(undefined); setPage(id); }} label="Collection pages" className="fk-coll__tabs" />
        <section className="fk-coll__page fk-panel" data-testid="collection-page" data-page={current.id} aria-label={current.title}>
          <header className="fk-coll__page-head">
            <h2 className="fk-coll__page-title">{current.title}</h2>
            <span className="fk-coll__count" data-testid="collection-page-count">{fmt(countCollected(current.defs, mine.collected))} of {fmt(current.defs.length)} collected</span>
          </header>
          {current.id === 'sets' && <p className="fk-coll__note">Each set is a row of its own: find one piece and the rest of its set show beside it.</p>}
          <div className="fk-coll__blocks">
            {current.blocks.map((b) => <BlockGrid key={b.key} block={b} mine={mine} onHover={onHover} onOpen={onOpen} />)}
          </div>
        </section>
      </div>
      {shownHover && hoverItem && (
        <Tooltip anchor={shownHover.el} title={hoverItem.name} terms={whereToFind(hoverItem)} className="fk-coll__tooltip">
          <SquareTip def={shownHover.def} state={squareState(layout, shownHover.def, mine.collected)} fresh={mine.fresh.has(shownHover.def)} />
        </Tooltip>
      )}
      <Modal open={!!openItem} onClose={() => setOpen(undefined)} eyebrow={openState === 'collected' ? 'Collected' : 'Not yet collected'} title={openItem?.name ?? ''} size="narrow" testId="collection-card" footer={<Button primary onClick={() => setOpen(undefined)}>Close</Button>}>
        {openItem && (
          <div className="fk-coll__card">
            <div className={`fk-coll__card-icon ${openState === 'hinted' ? 'is-hinted' : ''}`}><ItemIcon def={openItem.id} tier={openItem.rarity} scale={3} framed /></div>
            <p className="fk-coll__where" data-testid="collection-where">{whereToFind(openItem)}</p>
            {openState === 'collected'
              ? <>
                {firstAt !== undefined && <p className="fk-coll__first">First held {new Date(firstAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>}
                <ItemBody item={openItem} compact />
              </>
              : <SquareTip def={openItem.id} state={openState} fresh={false} />}
          </div>
        )}
      </Modal>
    </main>
  );
}
